import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useUserValues } from "../userprofile/UserContext";
import usePlanAccess from "../hooks/usePlanAccess";
import {
  GPT61_SOL_MODEL,
  migrateLegacyOpenAIModel,
  requiresOpenAIReasoning,
} from "../utils/openaiModels";

export const MODEL_OPTIONS = [
  {
    value: "gpt-6-luna",
    label: "GPT-6 Luna",
    description: "Rask og effektiv GPT-6-modell for chat",
  },
];

export const CHATBOT_OPENAI_MODEL_OPTIONS = [MODEL_OPTIONS[0]];

export const CHATBOT_ADMIN_OPENAI_MODEL_OPTIONS = [
  ...CHATBOT_OPENAI_MODEL_OPTIONS,
  {
    value: GPT61_SOL_MODEL,
    label: "GPT-6.1 Sol",
    description: "Admin-only toppmodell for kvalitetstunge chatoppgaver",
  },
];

export const AZURE_DIRECT_DEEPSEEK_MODEL = "deepseek-v4-flash";
const LEGACY_AZURE_DIRECT_DEEPSEEK_MODEL = "deepseek-v3.2";

export const CHATBOT_AZURE_MODEL_OPTIONS = [
  {
    value: "kimi-k2.5",
    label: "Kimi K2.5",
    description: "Admin-only Azure testmodell for tekstchat i denne appen",
  },
  {
    value: AZURE_DIRECT_DEEPSEEK_MODEL,
    label: "DeepSeek V4 Flash",
    description: "Admin-only Azure-modell med 1M kontekst for rask tekstchat",
  },
];

export const CHATBOT_ANTHROPIC_MODEL_OPTIONS = [
  {
    value: "claude-opus-5",
    label: "Claude Opus 5",
    description: "Admin-only Claude-modell for krevende resonnering og kode",
  },
  {
    value: "claude-sonnet-5",
    label: "Claude Sonnet 5",
    description:
      "Admin-only Claude-modell med god balanse mellom fart og kvalitet",
  },
  {
    value: "claude-fable-5",
    label: "Fable 5",
    description: "Admin-only Claude-modell for avansert tekstchat",
  },
];

export const AZURE_DIRECT_PROVIDER = "azure_direct";
export const LEGACY_AZURE_KIMI_PROVIDER = "azure_direct_kimi";
export const ANTHROPIC_PROVIDER = "anthropic";

const CHATBOT_OPENAI_MODEL_VALUES = CHATBOT_OPENAI_MODEL_OPTIONS.map(
  (option) => option.value,
);
const CHATBOT_ADMIN_OPENAI_MODEL_VALUES =
  CHATBOT_ADMIN_OPENAI_MODEL_OPTIONS.map((option) => option.value);
const CHATBOT_AZURE_MODEL_VALUES = CHATBOT_AZURE_MODEL_OPTIONS.map(
  (option) => option.value,
);
const CHATBOT_ANTHROPIC_MODEL_VALUES = CHATBOT_ANTHROPIC_MODEL_OPTIONS.map(
  (option) => option.value,
);
const DEEPSEEK_REASONING_VALUES = ["low", "high", "max"];

const getDefaultModelForProvider = (experimentalProvider, fallbackModel) => {
  if (experimentalProvider === AZURE_DIRECT_PROVIDER) {
    return CHATBOT_AZURE_MODEL_OPTIONS[0].value;
  }
  if (experimentalProvider === ANTHROPIC_PROVIDER) {
    return CHATBOT_ANTHROPIC_MODEL_OPTIONS[0].value;
  }
  return fallbackModel || CHATBOT_OPENAI_MODEL_OPTIONS[0].value;
};

const isAzureDeepSeekConfig = (experimentalProvider, model) =>
  experimentalProvider === AZURE_DIRECT_PROVIDER &&
  model === AZURE_DIRECT_DEEPSEEK_MODEL;

const getDefaultReasoningEffortForConfig = (experimentalProvider, model) =>
  isAzureDeepSeekConfig(experimentalProvider, model)
    ? "low"
    : DEFAULT_REASONING_EFFORT;

const normalizeReasoningEffortForConfig = (
  experimentalProvider,
  model,
  reasoningEffort,
) => {
  if (isAzureDeepSeekConfig(experimentalProvider, model)) {
    if (DEEPSEEK_REASONING_VALUES.includes(reasoningEffort)) {
      return reasoningEffort;
    }
    if (reasoningEffort === "medium" || reasoningEffort === "xhigh") {
      return "high";
    }
    return "low";
  }
  if (
    reasoningEffort === "minimal" ||
    (requiresOpenAIReasoning(model) && reasoningEffort === "none")
  ) {
    return DEFAULT_REASONING_EFFORT;
  }
  const options = experimentalProvider
    ? REASONING_OPTIONS
    : getOpenAIReasoningOptions(model);
  return options.some((option) => option.value === reasoningEffort)
    ? reasoningEffort
    : getDefaultReasoningEffortForConfig(experimentalProvider, model);
};

const normalizePersistedExperimentalProvider = (rawValue) => {
  if (!rawValue || rawValue === "openai") {
    return {
      experimentalProvider: null,
      impliedModel: null,
    };
  }
  if (rawValue === LEGACY_AZURE_KIMI_PROVIDER) {
    return {
      experimentalProvider: AZURE_DIRECT_PROVIDER,
      impliedModel: CHATBOT_AZURE_MODEL_OPTIONS[0].value,
    };
  }
  return {
    experimentalProvider: rawValue,
    impliedModel: null,
  };
};

export const REASONING_OPTIONS = [
  {
    value: "none",
    label: "Ingen",
    description: "Raskest svar uten ekstra resonnement",
  },
  {
    value: "low",
    label: "Lav",
    description: "Lett resonnement og lav kostnad",
  },
  {
    value: "medium",
    label: "Medium",
    description: "Balanserer fart og kvalitet (standard)",
  },
  {
    value: "high",
    label: "Høy",
    description: "Dypere resonnement for krevende problemer",
  },
  {
    value: "xhigh",
    label: "X-høy",
    description: "Maksimalt resonnement for ekstra krevende oppgaver",
  },
];

export const getOpenAIReasoningOptions = (model) =>
  [
    ...REASONING_OPTIONS,
    {
      value: "max",
      label: "Maks",
      description: "Størst resonnementbudsjett for ekstra krevende oppgaver",
    },
  ].filter(
    (option) => !requiresOpenAIReasoning(model) || option.value !== "none",
  );

export const VERBOSITY_OPTIONS = [
  {
    value: "low",
    label: "Lav",
    description: "Kortfattede svar",
  },
  {
    value: "medium",
    label: "Medium",
    description: "Balanserer korthet og detaljer (standard)",
  },
  {
    value: "high",
    label: "Høy",
    description: "Utførlige svar med flere detaljer",
  },
];

export const EXPERIMENTAL_PROVIDER_OPTIONS = [
  {
    value: "openai",
    label: "OpenAI",
    description: "Standardvalg i produksjon",
  },
  {
    value: AZURE_DIRECT_PROVIDER,
    label: "Azure",
    description: "Admin-only testløp for Kimi K2.5 og DeepSeek V4 Flash",
  },
  {
    value: ANTHROPIC_PROVIDER,
    label: "Claude (Anthropic)",
    description: "Admin-only Claude Opus 5, Sonnet 5 og Fable 5",
  },
];

export const DEFAULT_MODEL = CHATBOT_OPENAI_MODEL_OPTIONS[0].value;
export const PLUSS_DEFAULT_MODEL = DEFAULT_MODEL;
export const DEFAULT_REASONING_EFFORT = "low";
export const DEFAULT_VERBOSITY = "low";

export const defaultSettings = {
  model: DEFAULT_MODEL,
  reasoningEffort: DEFAULT_REASONING_EFFORT,
  verbosity: DEFAULT_VERBOSITY,
  experimentalProvider: null,
};

const STORAGE_KEY = "chatbotSettings";
const ADMIN_STORAGE_KEY = "chatbotSettingsAdmin";

const persistSettings = (storageKey, nextSettings) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(nextSettings));
  } catch (error) {
    console.warn("Failed to persist chatbot settings", error);
  }
};

const readPersistedSettings = (
  fallbackSettings = defaultSettings,
  {
    storageKey = STORAGE_KEY,
    fallbackStorageKey = null,
    openAiModelValues = CHATBOT_OPENAI_MODEL_VALUES,
  } = {},
) => {
  const savedSettings =
    localStorage.getItem(storageKey) ||
    (fallbackStorageKey ? localStorage.getItem(fallbackStorageKey) : null);

  if (!savedSettings) {
    return { ...fallbackSettings };
  }

  try {
    const parsedSettings = JSON.parse(savedSettings);
    const {
      model: persistedModelRaw,
      reasoningEffort: persistedReasoningEffort,
      verbosity: persistedVerbosity,
      experimentalProvider: persistedExperimentalProviderRaw,
      ...rest
    } = parsedSettings ?? {};
    delete rest.temperature;
    delete rest.mode;
    delete rest.quality;
    delete rest.format;
    delete rest.searchEnabled;

    const persistedModelAliases = {
      [LEGACY_AZURE_DIRECT_DEEPSEEK_MODEL]: AZURE_DIRECT_DEEPSEEK_MODEL,
    };
    const persistedModel =
      persistedModelAliases[persistedModelRaw] ||
      migrateLegacyOpenAIModel(persistedModelRaw);
    const {
      experimentalProvider: persistedExperimentalProvider,
      impliedModel: impliedExperimentalModel,
    } = normalizePersistedExperimentalProvider(
      persistedExperimentalProviderRaw,
    );
    const isValidVerbosity = VERBOSITY_OPTIONS.some(
      (option) => option.value === persistedVerbosity,
    );
    const isValidExperimentalProvider =
      !persistedExperimentalProvider ||
      EXPERIMENTAL_PROVIDER_OPTIONS.some(
        (option) => option.value === persistedExperimentalProvider,
      );
    const normalizedExperimentalProvider = isValidExperimentalProvider
      ? persistedExperimentalProvider
      : fallbackSettings.experimentalProvider;
    const providerModelValueSet =
      normalizedExperimentalProvider === AZURE_DIRECT_PROVIDER
        ? CHATBOT_AZURE_MODEL_VALUES
        : normalizedExperimentalProvider === ANTHROPIC_PROVIDER
          ? CHATBOT_ANTHROPIC_MODEL_VALUES
          : openAiModelValues;
    const resolvedPersistedModel = persistedModel || impliedExperimentalModel;
    const isValidModel = providerModelValueSet.includes(resolvedPersistedModel);
    const normalizedModel = isValidModel
      ? resolvedPersistedModel
      : getDefaultModelForProvider(
          normalizedExperimentalProvider,
          fallbackSettings.model,
        );
    const fallbackReasoning = getDefaultReasoningEffortForConfig(
      normalizedExperimentalProvider,
      normalizedModel,
    );

    return {
      ...fallbackSettings,
      ...rest,
      model: normalizedModel,
      reasoningEffort: normalizeReasoningEffortForConfig(
        normalizedExperimentalProvider,
        normalizedModel,
        persistedReasoningEffort || fallbackReasoning,
      ),
      verbosity: isValidVerbosity
        ? persistedVerbosity
        : fallbackSettings.verbosity,
      experimentalProvider: normalizedExperimentalProvider,
    };
  } catch (error) {
    console.warn("Failed to parse persisted chatbot settings", error);
    return { ...fallbackSettings };
  }
};

const readLearnerSettings = (fallbackSettings, hasPaidAccess) => {
  if (!hasPaidAccess) return { ...fallbackSettings };
  const persisted = readPersistedSettings(fallbackSettings);
  return {
    ...fallbackSettings,
    model: CHATBOT_OPENAI_MODEL_VALUES.includes(persisted.model)
      ? persisted.model
      : fallbackSettings.model,
    experimentalProvider: null,
  };
};

export const ChatBotContext = createContext({
  settings: defaultSettings,
  updateSettings: () => {},
});

export const useChatBot = () => useContext(ChatBotContext);

export const ChatBotContextProvider = ({ children }) => {
  const { isAdmin, loading } = useUserValues() || {};
  const { isFree, planKnown } = usePlanAccess() || {};
  const hasPaidAccess = planKnown === true && isFree === false;
  const resolvedDefaults = useMemo(() => {
    if (isAdmin) return defaultSettings;
    if (hasPaidAccess) {
      return {
        ...defaultSettings,
        model: PLUSS_DEFAULT_MODEL,
        reasoningEffort: "low",
      };
    }
    return defaultSettings;
  }, [hasPaidAccess, isAdmin]);
  const [settings, setSettings] = useState(() => {
    if (isAdmin) {
      return readPersistedSettings(resolvedDefaults, {
        storageKey: ADMIN_STORAGE_KEY,
        fallbackStorageKey: STORAGE_KEY,
        openAiModelValues: CHATBOT_ADMIN_OPENAI_MODEL_VALUES,
      });
    }
    return readLearnerSettings(resolvedDefaults, hasPaidAccess);
  });

  useEffect(() => {
    if (loading) return;
    if (isAdmin) {
      setSettings((prevSettings) => {
        const nextSettings = readPersistedSettings(resolvedDefaults, {
          storageKey: ADMIN_STORAGE_KEY,
          fallbackStorageKey: STORAGE_KEY,
          openAiModelValues: CHATBOT_ADMIN_OPENAI_MODEL_VALUES,
        });
        const hasChanges = Object.keys(nextSettings).some(
          (key) => prevSettings?.[key] !== nextSettings[key],
        );

        persistSettings(ADMIN_STORAGE_KEY, nextSettings);

        if (!hasChanges) {
          return prevSettings;
        }

        return nextSettings;
      });
      return;
    }

    setSettings((prevSettings) => {
      const nextSettings = readLearnerSettings(resolvedDefaults, hasPaidAccess);
      const hasChanges = Object.keys(nextSettings).some(
        (key) => prevSettings?.[key] !== nextSettings[key],
      );

      // Rewrite retired or disallowed stored models even when the normalized
      // in-memory value already matches the initial state.
      persistSettings(STORAGE_KEY, nextSettings);

      if (!hasChanges) {
        return prevSettings;
      }

      return nextSettings;
    });
  }, [hasPaidAccess, isAdmin, loading, resolvedDefaults]);

  // Update settings and persist in localStorage (excluding temperature if present)
  const updateSettings = (newSettings) => {
    if (!isAdmin) {
      const requestedKeys = Object.keys(newSettings || {});
      const requestedModel = newSettings?.model;
      if (
        !hasPaidAccess ||
        requestedKeys.length !== 1 ||
        requestedKeys[0] !== "model" ||
        !CHATBOT_OPENAI_MODEL_VALUES.includes(requestedModel)
      ) {
        return;
      }
      setSettings((prevSettings) => {
        const updatedSettings = {
          ...prevSettings,
          model: requestedModel,
          experimentalProvider: null,
        };
        persistSettings(STORAGE_KEY, updatedSettings);
        return updatedSettings;
      });
      return;
    }

    setSettings((prevSettings) => {
      const rest = { ...newSettings };
      delete rest.temperature;
      delete rest.mode;
      delete rest.quality;
      delete rest.format;
      delete rest.searchEnabled;
      const nextExperimentalProvider = Object.prototype.hasOwnProperty.call(
        rest,
        "experimentalProvider",
      )
        ? rest.experimentalProvider
        : prevSettings.experimentalProvider;
      const nextModel = Object.prototype.hasOwnProperty.call(rest, "model")
        ? rest.model
        : prevSettings.model;
      const providerModelValueSet =
        nextExperimentalProvider === AZURE_DIRECT_PROVIDER
          ? CHATBOT_AZURE_MODEL_VALUES
          : nextExperimentalProvider === ANTHROPIC_PROVIDER
            ? CHATBOT_ANTHROPIC_MODEL_VALUES
            : CHATBOT_ADMIN_OPENAI_MODEL_VALUES;
      const normalizedNextModel = providerModelValueSet.includes(nextModel)
        ? nextModel
        : getDefaultModelForProvider(nextExperimentalProvider);
      const requestedReasoning = Object.prototype.hasOwnProperty.call(
        rest,
        "reasoningEffort",
      )
        ? rest.reasoningEffort
        : prevSettings.reasoningEffort;
      const switchingIntoDeepSeek =
        !isAzureDeepSeekConfig(
          prevSettings.experimentalProvider,
          prevSettings.model,
        ) &&
        isAzureDeepSeekConfig(nextExperimentalProvider, normalizedNextModel);
      const normalizedReasoning = switchingIntoDeepSeek
        ? "low"
        : normalizeReasoningEffortForConfig(
            nextExperimentalProvider,
            normalizedNextModel,
            requestedReasoning,
          );
      const updatedSettings = {
        ...prevSettings,
        ...rest,
        model: normalizedNextModel,
        reasoningEffort: normalizedReasoning,
      };
      persistSettings(ADMIN_STORAGE_KEY, updatedSettings);
      return updatedSettings;
    });
  };

  const contextValue = {
    settings,
    updateSettings,
  };

  return (
    <ChatBotContext.Provider value={contextValue}>
      {children}
    </ChatBotContext.Provider>
  );
};

export default ChatBotContextProvider;
